import React, { useEffect, useRef, useState } from "react";
import { Mic, Square, Trash2, Play, Pause } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

/**
 * VoiceNoteRecorder — 30-second max audio capture using MediaRecorder.
 * Emits { blob, mime, duration_sec } via onRecorded.
 * Provides preview + remove. The parent uploads the blob on submit.
 */
export function VoiceNoteRecorder({ onRecorded, existingBlobUrl, onRemove }) {
  const { t, lang } = useLang();
  const [state, setState] = useState("idle"); // idle | recording | recorded
  const [elapsed, setElapsed] = useState(0);
  const [blobUrl, setBlobUrl] = useState(existingBlobUrl || null);
  const [playing, setPlaying] = useState(false);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const audioRef = useRef(null);

  useEffect(() => () => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (recorderRef.current && recorderRef.current.state === "recording") {
      recorderRef.current.stop();
    }
  }, []);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "audio/mp4";
      const rec = new MediaRecorder(stream, { mimeType: mime });
      chunksRef.current = [];
      rec.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      rec.onstop = () => {
        stream.getTracks().forEach((tr) => tr.stop());
        const blob = new Blob(chunksRef.current, { type: mime });
        const url = URL.createObjectURL(blob);
        setBlobUrl(url);
        setState("recorded");
        onRecorded?.({ blob, mime, duration_sec: elapsed });
      };
      recorderRef.current = rec;
      rec.start();
      setState("recording");
      setElapsed(0);
      timerRef.current = setInterval(() => {
        setElapsed((e) => {
          if (e + 1 >= 30) { stop(); return 30; }
          return e + 1;
        });
      }, 1000);
    } catch (err) {
      alert(lang === "hi" ? "माइक्रोफ़ोन नहीं मिला।" : "Microphone not available.");
    }
  };

  const stop = () => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (recorderRef.current && recorderRef.current.state === "recording") {
      recorderRef.current.stop();
    }
  };

  const removeRecording = () => {
    if (blobUrl) URL.revokeObjectURL(blobUrl);
    setBlobUrl(null);
    setState("idle");
    setElapsed(0);
    onRemove?.();
  };

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (playing) { audioRef.current.pause(); setPlaying(false); }
    else { audioRef.current.play(); setPlaying(true); }
  };

  const label = lang === "hi" ? "आवाज़ नोट (30 सेकंड, वैकल्पिक)" : "Voice note (30s, optional)";

  return (
    <div className="mt-2" data-testid="voice-note-widget">
      <div className="text-xs uppercase tracking-widest font-bold text-[#4A627A] mb-2">{label}</div>
      {state === "idle" && !blobUrl && (
        <button
          data-testid="voice-start-btn"
          onClick={start}
          type="button"
          className="tap-target w-full border-2 border-dashed border-[#1B3A5C]/25 rounded-md py-3 flex items-center justify-center gap-2 text-[#4A627A] text-sm"
        >
          <Mic size={16} /> {lang === "hi" ? "रिकॉर्ड करें" : "Record"}
        </button>
      )}
      {state === "recording" && (
        <div data-testid="voice-recording" className="w-full border border-red-300 bg-red-50 rounded-md py-3 px-4 flex items-center gap-3">
          <span className="w-3 h-3 rounded-full bg-red-600 animate-pulse" />
          <div className="flex-1 text-sm font-semibold text-red-800">
            {lang === "hi" ? "रिकॉर्ड हो रहा है…" : "Recording…"} <span className="ml-1">{elapsed}s / 30s</span>
          </div>
          <button
            data-testid="voice-stop-btn"
            onClick={stop}
            type="button"
            className="tap-target px-3 py-1 rounded-md bg-red-600 text-white text-xs font-semibold flex items-center gap-1"
          >
            <Square size={12} /> {lang === "hi" ? "रोकें" : "Stop"}
          </button>
        </div>
      )}
      {blobUrl && state !== "recording" && (
        <div className="w-full border border-[#1B3A5C]/15 rounded-md py-2 px-3 flex items-center gap-3">
          <audio ref={audioRef} src={blobUrl} onEnded={() => setPlaying(false)} />
          <button
            data-testid="voice-play-btn"
            onClick={togglePlay}
            type="button"
            className="tap-target w-9 h-9 rounded-full flex items-center justify-center"
            style={{ background: "var(--brand-navy)", color: "white" }}
          >
            {playing ? <Pause size={14} /> : <Play size={14} />}
          </button>
          <div className="flex-1 text-sm text-[#4A627A]">
            {lang === "hi" ? "रिकॉर्डिंग तैयार" : "Recording ready"} · {elapsed || "≤30"}s
          </div>
          <button
            data-testid="voice-remove-btn"
            onClick={removeRecording}
            type="button"
            className="text-red-600 tap-target p-1"
          >
            <Trash2 size={14} />
          </button>
        </div>
      )}
    </div>
  );
}
